import media from "@/config/media";

// Complete three-week rotating lunch menu. Every field is editable in one place.
// Values marked null were not supplied and are intentionally not invented.
// Every meal carries a Parent Note and a Kid Note, per our food standards.

export const menuWeeks = [
  {
    id: "week-1",
    number: 1,
    title: "Comfort & Home Staples",
    summary:
      "Familiar, easy-to-digest Indian favourites using gentle spices, lentils, grains and comforting flavours.",
    dailyRange: {
      calories: "≈ 380–430 kcal",
      protein: "≈ 11–14 g",
      fibre: "≈ 5–7 g",
    },
    meals: [
      {
        day: "Monday",
        name: "Classic Varan Bhaat & Nutty Bite",
        label: "Comfort staple",
        description:
          "Soft steamed Indrayani rice with golden tuvar dal, turmeric and A2 cow ghee, served with a soft jaggery and roasted-sesame energy bite.",
        ingredients: [
          "Indrayani rice",
          "Tuvar dal",
          "Turmeric",
          "A2 cow ghee",
          "Organic jaggery",
          "Roasted sesame",
        ],
        parentNote:
          "A grain and a lentil in their simplest form, cooked with ghee and turmeric. The sweet bite uses jaggery and sesame, not refined sugar.",
        kidNote: "Soft rice and warm yellow dal — the meal that makes everything feel alright.",
        nutrition: { calories: 395, protein: 11.5, carbs: 55, fat: 11, fibre: 5.2 },
        vegetarian: true,
        allergens: ["Sesame", "Milk (ghee)"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.varanBhaat,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Steam or microwave for 2–3 minutes until hot throughout.",
        availability: "Available",
      },
      {
        day: "Tuesday",
        name: "Whole Moong Gravy & Khapli Sheera",
        label: "Sprouted protein",
        description:
          "Sprouted green moong simmered in mild cumin-tomato gravy over ghee rice, paired with a soft Khapli wheat and jaggery sheera.",
        ingredients: [
          "Sprouted green moong",
          "Tomato",
          "Cumin",
          "Rice",
          "A2 cow ghee",
          "Khapli wheat",
          "Organic jaggery",
        ],
        parentNote:
          "The moong is sprouted before cooking, which is the traditional way to make whole pulses easier on young stomachs. Khapli is an heirloom wheat.",
        kidNote: "Little sprouted beans in a mild tomato gravy, with something sweet to finish.",
        nutrition: { calories: 420, protein: 13.8, carbs: 59, fat: 12, fibre: 7.3 },
        vegetarian: true,
        allergens: ["Wheat (gluten)", "Milk (ghee)"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.moongSheera,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Steam or microwave for 2–3 minutes until hot throughout.",
        availability: "Available",
      },
      {
        day: "Wednesday",
        name: "Methi Thepla & Aloo Sabzi",
        label: "Hand-held lunch",
        description:
          "Soft whole-wheat and fenugreek flatbreads served with mild cumin potatoes and homemade cucumber-mint curd raita.",
        ingredients: [
          "Whole wheat flour",
          "Fenugreek leaves",
          "Potato",
          "Cumin",
          "Curd",
          "Cucumber",
          "Mint",
        ],
        parentNote:
          "Greens are worked into the dough rather than served on the side, which is how most children will actually eat fenugreek.",
        kidNote: "Soft green flatbreads you can roll up, with cool minty curd to dip.",
        nutrition: { calories: 385, protein: 10.2, carbs: 51, fat: 12.5, fibre: 5.8 },
        vegetarian: true,
        allergens: ["Wheat (gluten)", "Milk (curd)"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.theplaAloo,
        storage: "Keep refrigerated below 5°C. Keep the raita chilled until serving.",
        reheating: "Warm the thepla on a dry pan for 30 seconds a side. Serve the raita cold.",
        availability: "Available",
      },
      {
        day: "Thursday",
        name: "Ragi Vegetable Idli & Lentil Sambar",
        label: "Steamed & millet-based",
        description:
          "Steamed ragi idlis infused with grated carrot and peas, served with mild vegetable tuvar-dal sambar and coconut chutney.",
        ingredients: [
          "Ragi",
          "Rice",
          "Urad dal",
          "Carrot",
          "Green peas",
          "Tuvar dal",
          "Fresh coconut",
        ],
        parentNote:
          "Steamed rather than fried, with vegetables grated into the batter. Finger millet replaces part of the rice.",
        kidNote: "Little steamed cakes with carrot inside — dunk them in the sambar.",
        nutrition: { calories: 370, protein: 12.6, carbs: 54, fat: 8.5, fibre: 6.8 },
        vegetarian: true,
        allergens: ["Coconut"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.ragiIdli,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Steam the idlis for 3 minutes. Heat the sambar until hot throughout.",
        availability: "Available",
      },
      {
        day: "Friday",
        name: "Coconut Bottle Gourd Dal & Jeera Rice",
        label: "Light & hydrating",
        description:
          "Bottle gourd and yellow moong dal cooked with fresh coconut and mild cashew-curry-leaf tempering, served with cumin rice.",
        ingredients: [
          "Bottle gourd",
          "Yellow moong dal",
          "Fresh coconut",
          "Cashew",
          "Curry leaves",
          "Cumin",
          "Rice",
        ],
        parentNote:
          "Bottle gourd has a high water content and a very mild flavour, which is why it suits the end of the week.",
        kidNote: "A light, creamy dal with cumin rice — easy on a Friday tummy.",
        nutrition: { calories: 410, protein: 11.8, carbs: 58, fat: 12.3, fibre: 8.1 },
        vegetarian: true,
        allergens: ["Tree nuts (cashew)", "Coconut"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.laukiDal,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Steam or microwave for 2–3 minutes until hot throughout.",
        availability: "Available",
      },
    ],
  },
  {
    id: "week-2",
    number: 2,
    title: "Warm & Wholesome Feasts",
    summary:
      "Comforting meals using pumpkin, sprouted lentils, millet, moringa, paneer and gentle Indian flavours.",
    dailyRange: {
      calories: "≈ 410–450 kcal",
      protein: "≈ 12–16 g",
      fibre: "≈ 6–8 g",
    },
    meals: [
      {
        day: "Monday",
        name: "Pumpkin Soup & Sprouted Matki",
        label: "Vegetable forward",
        description:
          "Creamy roasted pumpkin and carrot soup with butter, paired with coconut sprouted matki and baked ragi crisps.",
        ingredients: [
          "Pumpkin",
          "Carrot",
          "Butter",
          "Sprouted matki",
          "Fresh coconut",
          "Ragi",
        ],
        parentNote:
          "The crisps are baked rather than fried. The matki is sprouted, then cooked soft with coconut.",
        kidNote: "Bright orange soup with crunchy crisps to dip in it.",
        nutrition: { calories: 380, protein: 12.4, carbs: 48, fat: 10.5, fibre: 7.5 },
        vegetarian: true,
        allergens: ["Milk (butter)", "Coconut"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.pumpkinMatki,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Heat the soup gently on the stove or microwave for 2 minutes.",
        availability: "Available",
      },
      {
        day: "Tuesday",
        name: "Moong Khichdi, Kadhi & Phulka",
        label: "Easy on the tummy",
        description:
          "Ghee-rich moong dal khichdi served with mild buttermilk kadhi, curry leaves and hing, accompanied by one mini butter phulka.",
        ingredients: [
          "Moong dal",
          "Rice",
          "A2 cow ghee",
          "Buttermilk",
          "Gram flour",
          "Curry leaves",
          "Asafoetida",
          "Whole wheat flour",
        ],
        parentNote:
          "The dish Indian households turn to when a child is off their food. Cooked soft, lightly spiced, nothing sharp.",
        kidNote: "Soft khichdi with a gentle buttermilk curry and a little buttered phulka.",
        nutrition: { calories: 425, protein: 13.2, carbs: 58, fat: 10.8, fibre: 6.1 },
        vegetarian: true,
        allergens: ["Milk", "Wheat (gluten)"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.khichdiKadhi,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Add a splash of water and heat for 2–3 minutes until hot throughout.",
        availability: "Available",
      },
      {
        day: "Wednesday",
        name: "Ghee-Garlic Mung Dal, Paneer Bhurji & Soft Rotis",
        label: "Paneer protein",
        description:
          "Plain yellow mung dal tempered with ghee and golden garlic, served with paneer bhurji and soft rotis.",
        ingredients: [
          "Yellow mung dal",
          "A2 cow ghee",
          "Garlic",
          "Paneer",
          "Tomato",
          "Green peas",
          "Whole wheat flour",
        ],
        parentNote:
          "Particularly suited to children aged roughly three to six. Two protein sources — dal and paneer — with soft rotis rather than rice.",
        kidNote: "Crumbly paneer, buttery dal and soft rotis to tear and scoop.",
        nutrition: { calories: null, protein: null, carbs: null, fat: null, fibre: null },
        vegetarian: true,
        allergens: ["Milk (paneer, ghee)", "Wheat (gluten)"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        ageNote: "Best suited to ages 3–6. Portions are still offered for both age groups.",
        image: media.meals.moongPaneerBhurji,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Warm the rotis on a dry pan. Heat the dal and bhurji until hot throughout.",
        availability: "Available · nutrition being confirmed",
      },
      {
        day: "Thursday",
        name: "Moringa Vegetable Sambar & Rice",
        label: "Moringa greens",
        description:
          "A mild vegetable sambar with fresh moringa leaves, served with steamed Indrayani rice for the younger bands and unpolished Kerala red rice for the older bands.",
        ingredients: [
          "Moringa (drumstick) leaves",
          "Tuvar dal",
          "Seasonal vegetables",
          "Indrayani rice",
          "Kerala red rice",
          "Fresh coconut",
          "Curry leaves",
        ],
        parentNote:
          "Moringa leaves are cooked into the sambar so the greens arrive as part of the gravy. The rice is matched to the age group.",
        kidNote: "A tangy sambar with soft vegetables and rice to soak it all up.",
        nutrition: { calories: null, protein: null, carbs: null, fat: null, fibre: null },
        vegetarian: true,
        allergens: ["Coconut"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        ageNote: "Ages 2–7 receive Indrayani rice. Ages 8+ receive unpolished Kerala red rice.",
        image: media.meals.moringaSambar,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Heat for 3 minutes until hot throughout.",
        availability: "Available · nutrition being confirmed",
      },
      {
        day: "Friday",
        name: "Jaggery Puran Poli, Amti & Steamed Rice",
        label: "Festive familiar",
        description:
          "A mini whole-wheat flatbread filled with chana dal and organic jaggery, served with mild tangy Marathi amti and steamed rice.",
        ingredients: [
          "Whole wheat flour",
          "Chana dal",
          "Organic jaggery",
          "Tuvar dal",
          "Kokum",
          "Rice",
          "A2 cow ghee",
        ],
        parentNote:
          "The sweet element is a lentil-and-jaggery filling rather than a dessert added on top. Kokum provides the sourness instead of tamarind concentrate.",
        kidNote: "A sweet stuffed flatbread with a tangy dal — a proper Friday treat.",
        nutrition: { calories: 450, protein: 12.2, carbs: 60, fat: 14, fibre: 7.4 },
        vegetarian: true,
        allergens: ["Wheat (gluten)", "Milk (ghee)"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.puranPoli,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Warm the poli on a dry pan. Heat the amti until hot throughout.",
        availability: "Available",
      },
    ],
  },
  {
    id: "week-3",
    number: 3,
    title: "Coastal & Regional Wonders",
    summary:
      "Regional Indian dishes featuring millet, coconut, legumes, horse gram, black gram and mild coastal flavours.",
    dailyRange: {
      calories: "≈ 390–440 kcal",
      protein: "≈ 12–16 g",
      fibre: "≈ 6–8 g",
    },
    meals: [
      {
        day: "Monday",
        name: "Dry Sukhi Moong Dal & Kadhi Rice",
        label: "Gentle & soothing",
        description:
          "Lightly spiced dry yellow moong dal with ghee and curry leaves, paired with soothing yoghurt kadhi and steamed rice.",
        ingredients: [
          "Yellow moong dal",
          "A2 cow ghee",
          "Curry leaves",
          "Yoghurt",
          "Gram flour",
          "Rice",
        ],
        parentNote:
          "A dry dal rather than a gravy, so it holds well in a lunch box. The kadhi keeps the meal moist.",
        kidNote: "Buttery yellow dal with cool yoghurt curry and rice.",
        nutrition: { calories: 390, protein: 12.8, carbs: 53, fat: 10.3, fibre: 5.2 },
        vegetarian: true,
        allergens: ["Milk"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.sukhiMoong,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Heat for 2–3 minutes until hot throughout.",
        availability: "Available",
      },
      {
        day: "Tuesday",
        name: "Palak Paneer Bhurji with Phulka",
        label: "Iron & protein",
        description:
          "Soft paneer scrambled with finely chopped spinach and mild spices, served with warm phulkas.",
        ingredients: ["Paneer", "Spinach", "Whole wheat atta", "A2 cow ghee", "Tomato", "Mild spices"],
        parentNote:
          "Spinach is cooked into the paneer rather than served on the side, so iron and protein arrive in the same bite. Phulkas are rolled fresh and lightly ghee-brushed.",
        kidNote: "Soft cheesy scramble with a little green in it, and warm rotis to scoop it up.",
        nutrition: { calories: 395, protein: 16.4, carbs: 42, fat: 15.2, fibre: 6.1 },
        vegetarian: true,
        allergens: ["Milk", "Gluten"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.moongPaneerBhurji,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Heat the bhurji for 2 minutes. Warm the phulkas on a dry tawa.",
        availability: "Available",
      },
      {
        day: "Wednesday",
        name: "Horse Gram Curry & Rice",
        label: "High-protein legume",
        description:
          "Horse gram cooked in a mild Goan-style coconut curry, served with ghee rice and carrot salad.",
        ingredients: ["Horse gram", "Fresh coconut", "Rice", "A2 cow ghee", "Carrot"],
        parentNote:
          "Horse gram is one of the highest-protein pulses in Indian cooking and is cooked long and soft here.",
        kidNote: "A mild coconut curry with ghee rice and sweet carrot on the side.",
        nutrition: { calories: 410, protein: 15.2, carbs: 54, fat: 11, fibre: 8.5 },
        vegetarian: true,
        allergens: ["Coconut", "Milk (ghee)"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.horseGram,
        storage: "Keep refrigerated below 5°C. Keep the salad chilled until serving.",
        reheating: "Heat the curry and rice for 3 minutes. Serve the salad cold.",
        availability: "Available",
      },
      {
        day: "Thursday",
        name: "Masoor Dal with Roti & Rice, Sweet Potato Sheera",
        label: "Everyday favourite",
        description:
          "Soft masoor dal served with a roti and steamed rice, finished with a warm sweet potato sheera.",
        ingredients: [
          "Masoor dal",
          "Whole wheat flour",
          "Rice",
          "Sweet potato",
          "A2 cow ghee",
          "Organic jaggery",
          "Green cardamom",
        ],
        parentNote:
          "Masoor cooks down very soft, which suits younger children. The sheera uses sweet potato and jaggery instead of refined sugar.",
        kidNote: "Warm dal with roti and rice, then a sweet orange pudding at the end.",
        nutrition: { calories: null, protein: null, carbs: null, fat: null, fibre: null },
        vegetarian: true,
        allergens: ["Wheat (gluten)", "Milk (ghee)"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.masoorDal,
        storage: "Keep refrigerated below 5°C and consume the same day.",
        reheating: "Warm the roti on a dry pan. Heat the dal, rice and sheera until hot throughout.",
        availability: "Available · nutrition being confirmed",
      },
      {
        day: "Friday",
        name: "Himalayan Garhwali Chainsoo & Raita",
        label: "Regional protein",
        description:
          "Roasted black-gram dal served with rice and cooling pomegranate curd raita.",
        ingredients: ["Black gram (urad)", "Rice", "Curd", "Pomegranate", "A2 cow ghee"],
        parentNote:
          "The dal is dry-roasted before grinding, which is what gives chainsoo its deep flavour without heavy spice.",
        kidNote: "A rich dark dal with sweet pomegranate curd to cool it down.",
        nutrition: { calories: 425, protein: 16.8, carbs: null, fat: null, fibre: 8.2 },
        vegetarian: true,
        allergens: ["Milk"],
        ages: ["2-4", "5-7", "8-10", "11-plus"],
        image: media.meals.chainsoo,
        storage: "Keep refrigerated below 5°C. Keep the raita chilled until serving.",
        reheating: "Heat the dal and rice for 3 minutes. Serve the raita cold.",
        availability: "Available",
      },
    ],
  },
];

export const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export const nutritionMatrix = weekDays.map((day) => ({
  day,
  weeks: menuWeeks.map((week) => {
    const meal = week.meals.find((m) => m.day === day);
    return {
      weekNumber: week.number,
      name: meal.name,
      calories: meal.nutrition.calories,
      protein: meal.nutrition.protein,
    };
  }),
}));

export default menuWeeks;
